/**
 * Gemini Keys Admin Page
 * Gemini Key-ების ადმინისტრირების გვერდი
 */

import { useState, useEffect, useCallback } from 'react';
import { formatDateTime } from '../../utils/formatDate';
import { useNotification } from '@aiia/portal-core';
import {
  GeminiKey,
  GeminiKeyStats,
  GeminiAuditLog,
  getGeminiKeys,
  getGeminiStats,
  getGeminiAuditLogs,
  createGeminiKey,
  disableGeminiKey,
  enableGeminiKey,
  deleteGeminiKey,
  testGeminiKey,
  GEMINI_MODELS,
} from '../../features/api/geminiKeys';

export default function GeminiKeysPage() {
  const notification = useNotification();
  const [keys, setKeys] = useState<GeminiKey[]>([]);
  const [stats, setStats] = useState<GeminiKeyStats | null>(null);
  const [auditLogs, setAuditLogs] = useState<GeminiAuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showAuditModal, setShowAuditModal] = useState(false);

  // Create form state
  const [newKeyName, setNewKeyName] = useState('');
  const [newApiKey, setNewApiKey] = useState('');
  const [newDailyLimit, setNewDailyLimit] = useState(10000);
  const [newModel, setNewModel] = useState<string>(GEMINI_MODELS[0].value);
  const [testResult, setTestResult] = useState<{ valid: boolean; message: string } | null>(null);
  const [creating, setCreating] = useState(false);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [keysData, statsData] = await Promise.all([
        getGeminiKeys(),
        getGeminiStats(),
      ]);
      setKeys(keysData);
      setStats(statsData);
    } catch (error: unknown) {
      notification.error('Failed to load Gemini keys');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleTestKey = async () => {
    if (!newApiKey) return;
    try {
      const result = await testGeminiKey(newApiKey);
      setTestResult(result);
    } catch (error: unknown) {
      setTestResult({ valid: false, message: 'Failed to test key' });
    }
  };

  const handleCreateKey = async () => {
    if (!newKeyName || !newApiKey) {
      notification.error('Name and API Key are required');
      return;
    }

    try {
      setCreating(true);
      await createGeminiKey({
        name: newKeyName,
        apiKey: newApiKey,
        dailyLimit: newDailyLimit,
        model: newModel,
      });
      notification.success('Gemini key created successfully');
      setShowCreateModal(false);
      setNewKeyName('');
      setNewApiKey('');
      setNewDailyLimit(10000);
      setNewModel(GEMINI_MODELS[0].value);
      setTestResult(null);
      loadData();
    } catch (error: unknown) {
      notification.error('Failed to create Gemini key');
    } finally {
      setCreating(false);
    }
  };

  const handleDisableKey = async (keyId: string, keyName: string) => {
    const reason = prompt(`Reason for disabling "${keyName}":`);
    if (!reason) return;

    try {
      await disableGeminiKey(keyId, reason);
      notification.success('Key disabled successfully');
      loadData();
    } catch (error: unknown) {
      notification.error('Failed to disable key');
    }
  };

  const handleEnableKey = async (keyId: string) => {
    try {
      await enableGeminiKey(keyId);
      notification.success('Key enabled successfully');
      loadData();
    } catch (error: unknown) {
      notification.error('Failed to enable key');
    }
  };

  const handleDeleteKey = async (keyId: string, keyName: string) => {
    if (!confirm(`Are you sure you want to delete "${keyName}"? This action cannot be undone.`)) {
      return;
    }

    const reason = prompt('Reason for deletion:');
    try {
      await deleteGeminiKey(keyId, reason || undefined);
      notification.success('Key deleted successfully');
      loadData();
    } catch (error: unknown) {
      notification.error('Failed to delete key');
    }
  };

  const handleShowAudit = async () => {
    try {
      const logs = await getGeminiAuditLogs(undefined, 48);
      setAuditLogs(logs);
      setShowAuditModal(true);
    } catch (error: unknown) {
      notification.error('Failed to load audit logs');
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">Active</span>;
      case 'DISABLED':
        return <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-800">Disabled</span>;
      case 'RATE_LIMITED':
        return <span className="px-2 py-1 text-xs rounded-full bg-yellow-100 text-yellow-800">Rate Limited</span>;
      default:
        return <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-800">{status}</span>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gemini API Keys</h1>
          <p className="text-gray-600">Manage platform Gemini API keys for all users</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleShowAudit}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            View Audit Log
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Add Key
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-4 gap-4">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-500">Total Keys</div>
            <div className="text-2xl font-bold">{stats.totalKeys}</div>
            <div className="text-xs text-gray-400">
              {stats.activeKeys} active, {stats.disabledKeys} disabled
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-500">Daily Usage</div>
            <div className="text-2xl font-bold">{stats.totalUsage.toLocaleString()}</div>
            <div className="text-xs text-gray-400">of {stats.totalLimit.toLocaleString()} limit</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-500">Utilization</div>
            <div className="text-2xl font-bold">{stats.utilizationPercent.toFixed(1)}%</div>
            <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
              <div
                className="bg-blue-600 h-2 rounded-full"
                style={{ width: `${Math.min(stats.utilizationPercent, 100)}%` }}
              ></div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-500">Assigned Users</div>
            <div className="text-2xl font-bold">{stats.totalAssignments}</div>
            <div className="text-xs text-gray-400">
              {stats.rateLimitedKeys > 0 && (
                <span className="text-yellow-600">{stats.rateLimitedKeys} rate limited</span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Keys Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Model
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Usage
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Users
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Last Used
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {keys.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-6 py-12 text-center text-gray-500">
                  No Gemini keys configured. Add your first key to get started.
                </td>
              </tr>
            ) : (
              keys.map((key) => (
                <tr key={key.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="font-medium text-gray-900">{key.name}</div>
                    <div className="text-xs text-gray-500">Created by {key.createdByName}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-600">{key.model || 'gemini-2.5-flash'}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatusBadge(key.status)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {key.dailyUsageCount.toLocaleString()} / {key.dailyLimit.toLocaleString()}
                    </div>
                    <div className="w-24 bg-gray-200 rounded-full h-1.5 mt-1">
                      <div
                        className={`h-1.5 rounded-full ${
                          key.dailyUsageCount >= key.dailyLimit
                            ? 'bg-red-500'
                            : key.dailyUsageCount >= key.dailyLimit * 0.8
                            ? 'bg-yellow-500'
                            : 'bg-green-500'
                        }`}
                        style={{ width: `${Math.min((key.dailyUsageCount / key.dailyLimit) * 100, 100)}%` }}
                      ></div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {key.assignedUsersCount}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {key.lastUsedAt
                      ? formatDateTime(key.lastUsedAt)
                      : 'Never'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    {key.status === 'ACTIVE' || key.status === 'RATE_LIMITED' ? (
                      <button
                        onClick={() => handleDisableKey(key.id, key.name)}
                        className="text-yellow-600 hover:text-yellow-900 mr-3"
                      >
                        Disable
                      </button>
                    ) : (
                      <button
                        onClick={() => handleEnableKey(key.id)}
                        className="text-green-600 hover:text-green-900 mr-3"
                      >
                        Enable
                      </button>
                    )}
                    <button
                      onClick={() => handleDeleteKey(key.id, key.name)}
                      className="text-red-600 hover:text-red-900"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-4">Add Gemini API Key</h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name
                </label>
                <input
                  type="text"
                  value={newKeyName}
                  onChange={(e) => setNewKeyName(e.target.value)}
                  placeholder="e.g., gemini-key-1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Model
                </label>
                <select
                  value={newModel}
                  onChange={(e) => setNewModel(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                >
                  {GEMINI_MODELS.map((model) => (
                    <option key={model.value} value={model.value}>
                      {model.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  API Key
                </label>
                <div className="flex gap-2">
                  <input
                    type="password"
                    value={newApiKey}
                    onChange={(e) => {
                      setNewApiKey(e.target.value);
                      setTestResult(null);
                    }}
                    placeholder="Enter Gemini API key"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <button
                    onClick={handleTestKey}
                    disabled={!newApiKey}
                    className="px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50"
                  >
                    Test
                  </button>
                </div>
                {testResult && (
                  <div className={`mt-2 text-sm ${testResult.valid ? 'text-green-600' : 'text-red-600'}`}>
                    {testResult.message}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Daily Limit
                </label>
                <input
                  type="number"
                  value={newDailyLimit}
                  onChange={(e) => setNewDailyLimit(parseInt(e.target.value) || 10000)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Maximum number of API calls per day for this key
                </p>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setNewKeyName('');
                  setNewApiKey('');
                  setNewDailyLimit(10000);
                  setNewModel(GEMINI_MODELS[0].value);
                  setTestResult(null);
                }}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateKey}
                disabled={creating || !newKeyName || !newApiKey}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {creating ? 'Creating...' : 'Create Key'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Audit Modal */}
      {showAuditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[80vh] overflow-hidden">
            <div className="p-4 border-b flex justify-between items-center">
              <h2 className="text-lg font-bold text-gray-900">Audit Log (Last 48 hours)</h2>
              <button
                onClick={() => setShowAuditModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-4 overflow-y-auto max-h-[60vh]">
              {auditLogs.length === 0 ? (
                <p className="text-center text-gray-500 py-8">No audit logs found</p>
              ) : (
                <div className="space-y-3">
                  {auditLogs.map((log) => (
                    <div key={log.id} className="border rounded-lg p-3">
                      <div className="flex justify-between items-start">
                        <div>
                          <span className="font-medium text-gray-900">{log.action}</span>
                          {log.keyName && (
                            <span className="ml-2 text-gray-600">- {log.keyName}</span>
                          )}
                        </div>
                        <span className="text-xs text-gray-500">
                          {formatDateTime(log.performedAt)}
                        </span>
                      </div>
                      <div className="text-sm text-gray-600 mt-1">
                        By: {log.performedByName}
                      </div>
                      {log.reason && (
                        <div className="text-sm text-gray-500 mt-1">
                          Reason: {log.reason}
                        </div>
                      )}
                      {log.affectedUsersCount !== undefined && log.affectedUsersCount > 0 && (
                        <div className="text-sm text-yellow-600 mt-1">
                          Affected users: {log.affectedUsersCount}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
